import './style.css';
import './svg-controls.css';
import { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import CustomInput from './CustomInput';
import ValidityPeriodInput from './ValidityPeriodInput';
import React from 'react';
import { ReactComponent as Doctor } from '../../../../../assets/img/pharmacy/DOCTOR.svg';
import { ReactComponent as MedicalCommission } from '../../../../../assets/img/pharmacy/medicalCommission.svg';
import { ReactComponent as Recipe } from '../../../../../assets/img/pharmacy/recipe.svg';
import { ReactComponent as Stamp } from '../../../../../assets/img/pharmacy/stamp.svg';
import { ReactComponent as Stamp1 } from '../../../../../assets/img/pharmacy/Stamp1.svg';
import { ReactComponent as Stamp2 } from '../../../../../assets/img/pharmacy/Stamp2.svg';
import { ReactComponent as Stamp4 } from '../../../../../assets/img/pharmacy/Stamp4.svg';
import { ReactComponent as Special1 } from '../../../../../assets/img/pharmacy/Special1.svg';
import { ReactComponent as Special2 } from '../../../../../assets/img/pharmacy/Special2.svg';
import { ReactComponent as Special5 } from '../../../../../assets/img/pharmacy/Special5.svg';
import { ReactComponent as Recipe1 } from '../../../../../assets/img/pharmacy/Recipe1.svg';
import { ReactComponent as Doctor1 } from '../../../../../assets/img/pharmacy/Doctor1.svg';

interface Form148Props {
  onInputChange?: (id: number, value: string) => void;
  initialValues: InitialValuesType;
  onCheckboxStateChange?: (inputCheckboxStates: {
    inputs: { [key: number]: boolean };
    svgs: { [key: number]: boolean };
  }) => void;
  isShow?: boolean;
  isReadOnly?: boolean;
  isShowDeleteButton?: boolean;
  formDroppedItems?: SvgType[];
  handleDragStart?: (index: number, type: string) => void;
  handleDragOver?: () => void;
  handleDrop?: (event: React.DragEvent<HTMLDivElement>) => void;
  onSvgDelete?: (index: number, id: number) => void;
  isConstructor?: boolean;
  isChatPage?: boolean;
  userValidityInput?: string;
  onUserValidityInputChange?: (input: string) => void;
}
interface InitialValuesType {
  inputs?: {
    [key: number]: {
      value: string | undefined;
      right?: boolean;
      [key: string]: any;
    };
  };
  svgs?: Array<SvgType>;
}

interface SvgType {
  id: number;
  type: string;
  x: number;
  y: number;
  isWrongAnswer: boolean;
}

const tableStyles = {
  table: {
    width: '95%',
    margin: '20px 0',
    borderCollapse: 'collapse' as const,
    tableLayout: 'fixed' as const
  },
  td: {
    display: 'flex' as const,
    flexDirection: 'row' as const,
    alignItems: 'flex-start' as const
  },
  headerCell: {
    verticalAlign: 'top' as const,
    padding: '5px',
    width: '22%',
    textAlign: 'left' as const
  },
  contentCell: {
    verticalAlign: 'top' as const,
    padding: '5px',
    width: '600px',
    textAlign: 'left' as const,
    display: 'flex' as const,
    flexDirection: 'row' as const,
  }
};

const Form148 = forwardRef(({
  initialValues,
  onInputChange,
  formDroppedItems = [],
  handleDragStart,
  handleDragOver,
  handleDrop,
  onSvgDelete,
  isShow,
  isReadOnly,
  onCheckboxStateChange,
  isConstructor,
  isChatPage,
  userValidityInput,
  onUserValidityInputChange
}: Form148Props, ref) => {
  const [inputCheckboxStates, setInputCheckboxStates] = useState<{ [key: number]: boolean }>(() => {
    if (initialValues?.inputs) {
      const initialCheckboxStates = Object.fromEntries(
        Object.entries(initialValues.inputs).map(([id, input]) => [
          Number(id),
          input.right || false,
        ])
      );
      return initialCheckboxStates;
    }
    return {};
  });

  const [svgCheckboxStates, setSvgCheckboxStates] = useState<{ [key: number]: boolean }>(() => {
    if (initialValues?.svgs) {
      const initialSvgCheckboxStates = Object.fromEntries(
        initialValues.svgs.map((svg) => [svg.id, svg.isWrongAnswer || false])
      );
      return initialSvgCheckboxStates;
    }
    return {};
  });

  const [values, setValues] = useState(() => ({
    inputs: initialValues?.inputs || {},
  }));

  useEffect(() => {
    setValues((prevValues) => ({
      inputs: {
        ...prevValues.inputs,
        ...initialValues.inputs,
      },
    }));
  }, [initialValues.inputs]);

  const resetAllFields = () => {
    setValues({ inputs: {} });
    setInputCheckboxStates({});
    setSvgCheckboxStates({});
  };

  useImperativeHandle(ref, () => ({
    resetAllFields,
  }));

  const handleInputChangeLocal = (id: number, value: string) => {
    setValues((prev) => ({
      ...prev,
      inputs: {
        ...prev.inputs,
        [id]: {
          ...prev.inputs[id],
          value,
        },
      },
    }));
    onInputChange?.(id, value);
  };

  const handleInputCheckboxChange = (id: number) => {
    setInputCheckboxStates((prev) => {
      const newState = { ...prev, [id]: !prev[id] };

      setValues((prevValues) => ({
        inputs: {
          ...prevValues.inputs,
          [id]: {
            ...prevValues.inputs[id],
            right: newState[id],
          },
        },
      }));

      onCheckboxStateChange?.({ inputs: newState, svgs: svgCheckboxStates });
      return newState;
    });
  };

  const handleSvgCheckboxChange = (id: number) => {
    setSvgCheckboxStates((prev) => {
      const newState = { ...prev, [id]: !prev[id] };

      onCheckboxStateChange?.({ inputs: inputCheckboxStates, svgs: newState });
      return newState;
    });
  };

  const getSvgStyle = (type: string, x: number, y: number) => {
    const baseStyle = {
      position: 'absolute' as const,
      cursor: 'move'
    };

    switch (type) {
      case 'Doctor':
        return {
          ...baseStyle,
          left: `${x +30}px`,
          top: `${y + 230}px`,
          transform: 'scale(1)',
        };
      case 'MedicalCommission':
        return {
          ...baseStyle,
          left: `${x + 5}px`,
          top: `${y + 295}px`,
          transform: 'scale(1.1)',
        };
      case 'Recipe':
        return {
          ...baseStyle,
          left: `${x -30}px`,
          top: `${y + 290}px`,
          transform: 'scale(1)',
        };
      case 'Stamp':
        return {
          ...baseStyle,
          left: `${x - 210}px`,
          top: `${y + 80}px`,
          transform: 'scale(1.3)',
        };
      case 'Stamp1':
        return {
          ...baseStyle,
          left: `${x +20}px`,
          top: `${y + 175}px`,
          transform: 'scale(1)',
        };
      case 'Stamp2':
        return {
          ...baseStyle,
          left: `${x - 250}px`,
          top: `${y + 290}px`,
          transform: 'scale(1.3)',
        };
      case 'Stamp4':
        return {
          ...baseStyle,
          left: `${x - 165}px`,
          top: `${y + 45}px`,
          transform: 'scale(0.5)',
        };
      case 'Special1':
        return {
          ...baseStyle,
          left: `${x - 90}px`,
          top: `${y + 20}px`,
          transform: 'scale(0.6)',
        };
      case 'Special2':
        return {
          ...baseStyle,
          left: `${x -130}px`,
          top: `${y -40}px`,
          transform: 'scale(0.75)',
        };
      case 'Special5':
        return {
          ...baseStyle,
          left: `${x -90}px`,
          top: `${y -40}px`,
          transform: 'scale(0.5)',
        };
      case 'Recipe1':
        return {
          ...baseStyle,
          left: `${x + 75}px`,
          top: `${y - 20}px`,
          transform: 'scale(1.3)',
        };
      case 'Doctor1':
        return {
          ...baseStyle,
          left: `${x + 120}px`,
          top: `${y - 50}px`,
          transform: 'scale(1.1)',
        };
      default:
        return {
          ...baseStyle,
          left: `${x}px`,
          top: `${y}px`,
          transform: 'scale(1)',
        };
    }
  };

  return (
    <div
      style={{
        maxWidth: '1200px',
        margin: '0 auto',
        paddingBottom: 50,
        paddingTop: 30,
        border: '1px solid rgba(34, 34, 34, 0.31)',
        padding: 20,
        position: 'relative'
      }}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <div style={{ margin: '0 auto', marginBottom: 20 }}>
        <div style={{ textAlign: 'center', fontSize: '20px', fontWeight: 500, marginBottom: 8 }}>
          ФОРМА РЕЦЕПТУРНОГО БЛАНКА N 148-1/У-88
        </div>
        <table style={{ borderCollapse: 'collapse', fontSize: '16px', marginBottom: 0, maxWidth: 900, display: 'block', margin: '0 auto' }}>
          <tbody>
            <tr>
              <td style={{ border: '1px solid #888', padding: 6, width: '50%', verticalAlign: 'top' }}>
                Министерство здравоохранения<br />Российской Федерации
              </td>
              <td style={{ border: '1px solid #888', padding: 6, width: '50%', verticalAlign: 'top', height: '100%' }}>
                Код формы по ОКУД 3108805<br />
                Медицинская документация<br />
                Форма N 148-1/у-88<br />
                Утверждена приказом<br />
                Министерства здравоохранения<br />
                Российской Федерации<br />
                от 24 ноября 2021 г. N 1094н
              </td>
            </tr>
            <tr>
              <td style={{ border: '1px solid #888', padding: 6, verticalAlign: 'top' }}>
                Наименование (штамп)<br />медицинской организации
              </td>
            </tr>
            <tr>
              <td style={{ border: '1px solid #888', padding: 6, verticalAlign: 'top' }}>
                Наименование (штамп)<br />индивидуального предпринимателя<br />
                <span style={{ fontSize: '13px' }}>(указать адрес, номер и дату лицензии, наименование органа государственной власти, выдавшего лицензию)</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      {[...initialValues.svgs || [], ...formDroppedItems].map((svg, index) => (
        <div
          key={`svg-${svg.id}-${index}`}
          className="svg-wrapper"
          style={getSvgStyle(svg.type, svg.x, svg.y)}
          draggable={isConstructor}
          onDragStart={() => handleDragStart?.(index, 'response')}
        >
          {svg.type === 'Doctor' && <Doctor />}
          {svg.type === 'MedicalCommission' && <MedicalCommission />}
          {svg.type === 'Recipe' && <Recipe />}
          {svg.type === 'Stamp' && <Stamp />}
          {svg.type === 'Stamp1' && <Stamp1 />}
          {svg.type === 'Stamp2' && <Stamp2 />}
          {svg.type === 'Stamp4' && <Stamp4 />}
          {svg.type === 'Special1' && <Special1 />}
          {svg.type === 'Special2' && <Special2 />}
          {svg.type === 'Special5' && <Special5 />}
          {svg.type === 'Recipe1' && <Recipe1 />}
          {svg.type === 'Doctor1' && <Doctor1 />}
          <div className="svg-controls">
            {isShow && (
              <input
                type="checkbox"
                className="svg-checkbox"
                checked={svgCheckboxStates[svg.id] || false}
                onChange={() => handleSvgCheckboxChange(svg.id)}
              />
            )}
            {isConstructor && (
              <button
                className="svg-delete-button"
                onClick={() => onSvgDelete?.(index, svg.id)}
              >
                ×
              </button>
            )}
          </div>
        </div>
      ))}
      <div className={'pharmacy-input-block'}>
        <span>Серия</span>
        <CustomInput
          id={1}
          onChange={handleInputChangeLocal}
          inputCount={4}
          value={values.inputs?.[1]?.value ?? ''}
          disabled={isReadOnly}
        />
        <span>N</span>
        <CustomInput
          id={2}
          onChange={handleInputChangeLocal}
          inputCount={5}
          value={values.inputs?.[2]?.value ?? ''}
          disabled={isReadOnly}
        />
        {isShow && (
          <input
            id="checkbox-2"
            type="checkbox"
            checked={inputCheckboxStates[2] || false}
            onChange={() => handleInputCheckboxChange(2)}
          />
        )}
      </div>
      <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '5px', marginBottom: '10px', justifyContent: 'center' }}>
        <span>РЕЦЕПТ</span>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <span>"</span>
          <CustomInput
            id={3}
            onChange={handleInputChangeLocal}
            inputCount={2}
            value={values.inputs?.[3]?.value ?? ''}
            disabled={isReadOnly}
          />
          <span>"</span>
          <CustomInput
            id={4}
            onChange={handleInputChangeLocal}
            inputCount={4}
            value={values.inputs?.[4]?.value ?? ''}
            disabled={isReadOnly}
          />
          <span>20</span>
          <CustomInput
            id={5}
            onChange={handleInputChangeLocal}
            inputCount={2}
            value={values.inputs?.[5]?.value ?? ''}
            disabled={isReadOnly}
          />
          <span>г.</span>
          {isShow && (
            <input
              id="checkbox-5"
              type="checkbox"
              checked={inputCheckboxStates[5] || false}
              onChange={() => handleInputCheckboxChange(5)}
            />
          )}
        </div>
      </div>
      <div className={'date-input-description'}>
        <span>(дата оформления рецепта)</span>
      </div>

      <div className={'type-block'}>
        <div style={{ textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <span>(</span>
          <span
            style={{
              textDecoration: values.inputs?.[19]?.value === 'adult' ? 'underline' : 'none',
              cursor: 'pointer',
            }}
            onClick={() => !isReadOnly && handleInputChangeLocal(19, 'adult')}
          >
            взрослый
          </span>
          <span>, </span>
          <span
            style={{
              textDecoration: values.inputs?.[19]?.value === 'child' ? 'underline' : 'none',
              cursor: 'pointer',
              padding: '0px 4px'
            }}
            onClick={() => !isReadOnly && handleInputChangeLocal(19, 'child')}
          >
            детский
          </span>
          <span> - нужное подчеркнуть)</span>
          {isShow && (
            <input
              id="checkbox-19"
              type="checkbox"
              checked={inputCheckboxStates[19] || false}
              onChange={() => handleInputCheckboxChange(19)}
            />
          )}
        </div>
      </div>

      <div className={'fio-block-patient'}>
        <span>Фамилия, инициалы имени и отчества (последнее - при наличии) пациента</span>
        <div style={{ display: 'flex', flexDirection: 'row', width: '100%' }}>
          <input
            id="fio-patient"
            className={'date-born'}
            onChange={(event) => { handleInputChangeLocal(6, event.target.value); }}
            value={values.inputs?.[6]?.value ?? ''}
            type={'text'}
            disabled={isReadOnly}
            style={{ fontSize: '14pt', height: '25px' }}
          />
          {isShow && (
            <input
              id="checkbox-6"
              type="checkbox"
              checked={inputCheckboxStates[6] || false}
              onChange={() => handleInputCheckboxChange(6)}
            />
          )}
        </div>
      </div>
      {/* Дата рождения */}
      <div className={'pharmacy-date-born-block'}>
        <span>Дата рождения</span>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <span>"</span>
          <input
            id="date-born-day"
            type="text"
            style={{ width: '5%', borderBottom: '1px solid #000', borderTop: 'none', borderLeft: 'none', borderRight: 'none', height: 25, fontSize: '14pt' }}
            value={values.inputs?.[7]?.value ?? ''}
            onChange={(e) => {
              const val = e.target.value.replace(/\\D/g, '').slice(0, 2);
              handleInputChangeLocal(7, val);
            }}
            disabled={isReadOnly}
          />
          <span>"</span>
          <input
            id="date-born-month"
            type="text"
            style={{ width: '20%', borderBottom: '1px solid #000', borderTop: 'none', borderLeft: 'none', borderRight: 'none', height: 25, fontSize: '14pt' }}
            value={values.inputs?.[8]?.value ?? ''}
            onChange={(e) => {
              const val = e.target.value.replace(/\\D/g, '').slice(0, 8);
              handleInputChangeLocal(8, val);
            }}
            disabled={isReadOnly}
          />
          <span>20</span>
          <input
            id="date-born-year"
            type="text"
            style={{ width: '5%', borderBottom: '1px solid #000', borderTop: 'none', borderLeft: 'none', borderRight: 'none', height: 25, fontSize: '14pt' }}
            value={values.inputs?.[9]?.value ?? ''}
            onChange={(e) => {
              const val = e.target.value.replace(/\\D/g, '').slice(0, 2);
              handleInputChangeLocal(9, val);
            }}
            disabled={isReadOnly}
          />
          <span>г.</span>
          {isShow && (
            <input
              id="checkbox-9"
              type="checkbox"
              checked={inputCheckboxStates[9] || false}
              onChange={() => handleInputCheckboxChange(9)}
            />
          )}
        </div>
      </div>

      {/* Адрес места жительства */}
      <div style={{ marginTop: 10, width: '100%' }}>
        <span>
          Адрес места жительства или номер медицинской карты пациента
        </span>
        <input
          type="text"
          value={values.inputs?.[10]?.value ?? ''}
          onChange={(e) => handleInputChangeLocal(10, e.target.value)}
          disabled={isReadOnly}
          className={'address-input'}
          style={{ borderLeft: 'none', borderRight: 'none', borderTop: 'none', width: 600, fontSize: '14pt', height: '25px' }}
        />
        {isShow && (
          <input
            id="checkbox-10"
            type="checkbox"
            checked={inputCheckboxStates[10] || false}
            onChange={() => handleInputCheckboxChange(10)}
          />
        )}
      </div>

      <div style={{ margin: '10px 0' }}>
        <label>Фамилия, инициалы имени и отчества (последнее - при наличии) лечащего врача (фельдшера, акушерки)</label>
        <input
          type="text"
          style={{ width: '95%', borderBottom: '1px solid #000', borderTop: 'none', borderLeft: 'none', borderRight: 'none', height: 25, fontSize: '14pt' }}
          value={values.inputs?.[11]?.value ?? ''}
          onChange={(e) => handleInputChangeLocal(11, e.target.value)}
          disabled={isReadOnly}
        />
        {isShow && (
          <input
            type="checkbox"
            checked={inputCheckboxStates[11] || false}
            onChange={() => handleInputCheckboxChange(11)}
          />
        )}
      </div>

      {/* Таблица Rp */}
      <table style={tableStyles.table}>
        <tbody>
          <tr style={tableStyles.td}>
            <td style={tableStyles.headerCell}>руб.</td>
            <td style={tableStyles.headerCell}>коп.</td>
            <td style={tableStyles.headerCell}>Rp.</td>
          </tr>
          <tr style={tableStyles.td}  >
            <td style={tableStyles.contentCell}>
              <textarea
                rows={1}
                style={{ width: '95%', resize: 'none', fontSize: '14pt' }}
                value={values.inputs?.[12]?.value ?? ''}
                onChange={(e) => handleInputChangeLocal(12, e.target.value)}
                disabled={isReadOnly}
              />
              {isShow && (
                <input
                  type="checkbox"
                  checked={inputCheckboxStates[12] || false}
                  onChange={() => handleInputCheckboxChange(12)}
                />
              )}
            </td>
          </tr>
          <tr>
            <td style={tableStyles.contentCell}>
              <textarea
                rows={1}
                style={{ width: '95%', resize: 'none', fontSize: '14pt' }}
                value={values.inputs?.[13]?.value ?? ''}
                onChange={(e) => handleInputChangeLocal(13, e.target.value)}
                disabled={isReadOnly}
              />
              {isShow && (
                <input
                  type="checkbox"
                  checked={inputCheckboxStates[13] || false}
                  onChange={() => handleInputCheckboxChange(13)}
                />
              )}
            </td>
          </tr>
          <tr>
            <td style={tableStyles.contentCell}>
              <textarea
                rows={1}
                style={{ width: '95%', resize: 'none', fontSize: '14pt' }}
                value={values.inputs?.[18]?.value ?? ''}
                onChange={(e) => handleInputChangeLocal(18, e.target.value)}
                disabled={isReadOnly}
              />
              {isShow && (
                <input
                  type="checkbox"
                  checked={inputCheckboxStates[18] || false}
                  onChange={() => handleInputCheckboxChange(18)}
                />
              )}
            </td>
            <td style={tableStyles.contentCell}>
              <textarea
                rows={1}
                style={{ width: '95%', resize: 'none', fontSize: '14pt' }}
                value={values.inputs?.[14]?.value ?? ''}
                onChange={(e) => handleInputChangeLocal(14, e.target.value)}
                disabled={isReadOnly}
              />
              {isShow && (
                <input
                  type="checkbox"
                  checked={inputCheckboxStates[14] || false}
                  onChange={() => handleInputCheckboxChange(14)}
                />
              )}
            </td>
          </tr>
        </tbody>
      </table>

      {/* Подписи */}
      <div style={{ marginTop: 10 }}>
        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', gap: '5px', marginBottom: '10px', justifyContent: 'flex-start' }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '5px', marginBottom: '10px', width: '30%' }}>
            <span>Подпись и печать лечащего врача</span>
            <span>(подпись фельдшера, акушерки)</span>
          </div>
          <input
            type="text"
            value={values.inputs?.[15]?.value ?? ''}
            onChange={(e) => handleInputChangeLocal(15, e.target.value)}
            disabled={isReadOnly}
            style={{
              borderLeft: 'none',
              borderTop: 'none',
              borderRight: 'none',
              borderBottom: '1px dotted black',
              width: '20%',
              fontSize: '14pt',
              height: '25px'
            }}
          />
          {isShow && (
            <input
              id="checkbox-15"
              type="checkbox"
              checked={inputCheckboxStates[15] || false}
              onChange={() => handleInputCheckboxChange(15)}
            />
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <span>М.П.</span>
          </div>
        </div>

      </div>

      {/* <ValidityPeriodInput
        value={values.inputs?.[99]?.value ?? ''}
        onChange={handleInputChangeLocal}
        isReadOnly={isReadOnly}
        isChatPage={isChatPage}
        userValidityInput={userValidityInput}
        onUserValidityInputChange={onUserValidityInputChange}
        showCheckbox={isShow}
        onCheckboxChange={(checked) => handleInputCheckboxChange(99)}
        isCheckboxChecked={inputCheckboxStates[99] || false}
      /> */}
    </div>
  );
})

export default Form148